<template>
  <v-container fluid class="custom-container-details">
    <div>
      <v-row wrap no-gutters >
        <v-col
          v-if="!userCanEdit && !loading"
          cols="12"
          md="9"
        >
          <v-alert dense color="warning" style="color: black">{{ $lang.errors.readOnly }}</v-alert>
        </v-col>
        <v-col
          cols="12"
        >
          <v-tabs
            v-model="tab"
            class="custom-tab-design"
            background-color="transparent"
          >

            <v-tab
              :key="0"
              data-cy="tab-details"
              class="ml-2"
            >
              {{ $lang.labels.details }}
            </v-tab>
            <v-tab
              :key="1"
              data-cy="tab-permissions"
              class="ml-2"
            >
              {{ $lang.labels.permissions }}
            </v-tab>
            <v-tab
              :key="2"
              data-cy="tab-history"
              class="ml-2"
            >
              {{ $lang.labels.history }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab" class=" custom-bg pt-6">
            <v-tab-item
              :key="0"
              class="fill-height"
            >
              <v-card class="pa-1 fill-height custom-bg">
                <v-form
                  ref="form"
                  v-model="valid"
                >
                  <v-row wrap no-gutters justify="space-between" class="py-1">
                    <v-col
                      cols="12"
                      sm="9"
                      class="pr-sm-1"
                    >
                      <div class="d-inline-flex max-width">
                        <v-text-field
                          v-model="library.name"
                          outlined
                          dense
                          :label="$lang.labels.name"
                          required
                          class="required-asterisk copy-name-icon mb-3"
                          :rules="[
                            v => !!v.trim() || $lang.errors.required,
                            v => (v.trim().length > 1 && v.trim().length <= 250) || $lang.errors.nameTooShortOrLong
                          ]"
                          :persistent-hint="isEdit"
                          :hint="formatHint"
                          :readonly="!userCanEdit"
                          data-cy="library-name"
                          append-icon="mdi-vector-combine"
                          @click:append="copyName()"
                        ></v-text-field>
                      </div>
                    </v-col>
                    <v-col v-if="isEdit" cols="3" class="pl-sm-1 text-right pb-2">
                      <v-btn
                        outlined

                        color="primary"
                        :disabled="!userCanEdit"
                        @click="changeStatus()"
                      >
                        <v-icon
                          left
                          dark
                        >
                          {{ library.status === 'ACTIVE' ? 'mdi-toggle-switch-off ' : 'mdi-toggle-switch' }}
                        </v-icon>
                        {{ library.status === 'ACTIVE' ? $lang.actions.deactivate : $lang.actions.activate }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row wrap no-gutters :class="isEdit ? 'pb-0' : 'pb-2'">
                    <v-col
                      cols="12"
                      sm="9"
                    >
                      <v-select
                        v-model="library.type"
                        class="pr-1 required-asterisk"
                        :items="typesFormatted"
                        :label="$lang.labels.type"
                        outlined
                        dense
                        :rules="[v => !!v || $lang.labels.required]"
                      ></v-select>
                    </v-col>
                    <v-col
                      v-if="isEdit"
                      cols="12"
                      sm="9"
                      class="pb-2 pr-1"
                    >
                      <div class="d-flex align-center">
                        <v-text-field
                          ref="fileTextField"
                          v-model="file"
                          prepend-icon="mdi-paperclip"
                          outlined
                          solo
                          dense
                          readonly
                          hide-details
                          :label="$lang.actions.clickToImport"
                          class="min-w"
                          @click="startInput('fileInput')"
                          @click:prepend="startInput('fileInput')"
                        />
                        <input
                          ref="fileInput"
                          style="display: none;"
                          type="file"
                          :accept="fileTypesBasedOnLibType()"
                          @change="onFileChange($event)"
                        >
                        <v-btn
                          outlined
                          :disabled="!library.id || !fileData || lock || !userCanEdit"
                          color="accent"
                          :loading="loading"
                          class="button-default-width ml-2 color-black"
                          @click="uploadLibrary()"
                        >
                          <v-icon
                            left
                            dark
                          >
                            mdi-import
                          </v-icon>
                          {{ $lang.actions.import }}
                        </v-btn>
                        <v-btn
                          :disabled="!library.id || !library.filename || lock || !userCanEdit"
                          color="primary"
                          outlined
                          :loading="loading"
                          class="button-default-width ml-2 pl-3 color-black"
                          @click="downloadLibraryFunction()"
                        >
                          <v-icon
                            left
                            dark
                          >
                            mdi-download
                          </v-icon>
                          {{ $lang.actions.download }}
                        </v-btn>
                      </div>
                    </v-col>
                    <v-col
                      v-if="isEdit"
                      cols="12"
                      md="5"
                      class="mt-2"
                    >
                      <v-text-field
                        v-model="library.filename"
                        outlined
                        dense
                        readonly
                        :label="$lang.labels.filename"
                        data-cy="library-filename"
                        class="pr-md-1"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      v-if="isEdit"
                      cols="12"
                      md="4"
                      class="mt-2"
                    >
                      <v-text-field
                        v-model="library.size"
                        outlined
                        dense
                        readonly
                        :label="$lang.labels.sizeInBytes"
                        data-cy="library-size"
                        class="px-md-1"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      class="d-inline-flex"
                    >
                      <v-checkbox v-model="library.isGdprRelevant" :label="$lang.labels.gdpr"></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row no-gutters wrap justify="space-between">
                    <v-divider class="mb-4" />
                  </v-row>

                  <v-row no-gutters wrap justify="space-between">
                    <v-col
                      cols="6"
                      class="text-left"
                    >
                      <div class="d-inline-flex align-center">
                        <v-btn
                          outlined
                          color="primary"

                          :to="{ name: 'libraries', params: { lang: $lang.current_lang } }"
                        >
                          <v-icon
                            left
                            dark
                          >
                            mdi-arrow-left
                          </v-icon>
                          <span class="ml-1">{{ $lang.actions.back }}</span>
                        </v-btn>
                        <action-button-with-confirmation
                          v-if="isEdit"
                          outlined
                          :action-text="$lang.actions.areYouSureYouWantToDelete"
                          :action-text-suffix="library.name"
                          :title="$lang.actions.delete"
                          type="library"
                          :is-disabled="!userCanDelete"
                          :button-text="$lang.actions.delete"
                          :button-color="'error'"
                          :data-cy="'library-delete'"
                          :forced-option="true"
                          :trigger-force-logic="triggerForceLogic"
                          :regular-delete-errors-usages="regularDeleteErrorsUsages"
                          class="ml-2"
                          :delete-success="deleteSuccess"
                          @submit="deleteLibraryFunct($event)"
                          @closeDialog="''"
                          @closeAfterDelete="$router.push({ name: 'libraries' })"
                        />
                      </div>
                    </v-col>
                    <v-col
                      cols="6"
                      class="text-right"
                    >
                      <div class="d-inline-flex">
                        <v-btn
                          outlined
                          :disabled="!isFormValid || !isEdit"
                          color="primary"
                          class="ml-2 color-accent-text"
                          @click="copyCreateResource(library, 'libraryCreate', $router, '', $options.filters.formatDateTime(new Date()))"
                        >
                          <v-icon
                            left
                            dark
                          >
                            mdi-content-copy
                          </v-icon>
                          {{ $lang.actions.copyCreate }}
                        </v-btn>
                        <v-btn
                          :disabled="!isFormValid"
                          color="primary"
                          :loading="loading"
                          class="button-default-width ml-2"
                          data-cy="library-submit"
                          @click="submit()"
                        >
                          <v-icon
                            left
                            dark
                            class="mr-1"
                          >
                            mdi mdi-floppy
                          </v-icon>
                          {{ $lang.actions.submit }}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card>
            </v-tab-item>
            <v-tab-item
              :key="1"
              class="fill-height"
            >
              <v-card class="pa-2 fill-height custom-bg">
                <v-row wrap no-gutters class="pb-4">
                  <v-col cols="12" class="pb-2">
                    <div v-if="allRoles && allRoles.length > 0" style="width: 100%; height: 100%">
                      <h3 class="pb-1">{{ $lang.status.EDIT }}</h3>
                      <user-roles-select
                        :role="editRolesIds"
                        :options="allRoles"
                        data-cy="roles-edit"
                        :required="false"
                        :readonly="!userCanEdit"
                        @changeRoles="editRolesIds = $event"
                      ></user-roles-select>
                      <h3 class="pb-1">{{ $lang.status.USE }}</h3>
                      <user-roles-select
                        :role="useRolesIds"
                        :options="useRolePool"
                        data-cy="roles-use"
                        :required="false"
                        :readonly="!userCanEdit"
                        @changeRoles="useRolesIds = $event"
                      ></user-roles-select>
                      <h3 class="pb-1">{{ $lang.status.VIEW }}</h3>
                      <user-roles-select
                        :role="viewRolesIds"
                        :options="viewRolePool"
                        data-cy="roles-view"
                        :required="false"
                        :readonly="!userCanEdit"
                        @changeRoles="viewRolesIds = $event"
                      ></user-roles-select>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>
            <v-tab-item
              :key="2"
              class="fill-height"
            >
              <v-card class="py-1 fill-height custom-bg">
                <div style="width: 100%; height: 100%">
                  <v-row dense no-gutters>
                    <v-col cols="12">
                      <Table
                        :items="history"
                        @fetchHistory="fetchHistory($event)"
                        @restoreHistory="restoreLibrary($event)"
                      ></Table>
                    </v-col>
                  </v-row>
                </div>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </div>
    <multi-errors-snackbar
      :show="showSnackErrors"
      :errors="errorsForSnackbar"
      @close="showSnackErrors = false; errorsForSnackbar = []"
    />
  </v-container>
</template>

<script>
import Table from '@/components/ui/datatables/FlowyHistoryDatatable'
import {
  disableLibraryUsingGET as disableLibrary,
  enableLibraryUsingGET as enableLibrary,
  getLibraryByIdUsingGET as getLibrary,
  updateLibraryUsingPUT as updateLibrary,
  createLibraryUsingPOST as createLibrary,
  getHistoriesUsingGET as getHistories,
  deleteLibraryUsingDELETE as deleteLibrary,
  downloadLibraryUsingGET as downloadLibrary
} from '@/utils/api'
import auth from '@/auth'
import UserRolesSelect from '../../components/ui/UserRolesSelect'
import ActionButtonWithConfirmation from '@/components/ui/ActionButtonWithConfirmation.vue'
import MultiErrorsSnackbar from '@/components/ui/MultiErrorsSnackbar.vue'
import copy from 'copy-to-clipboard'
import { mapActions } from 'vuex'
import { copyCreateResource, getRolesWithoutAuth } from '@/utils/helpers'
import { bus } from '@/main'

export default {
  components: {
    ActionButtonWithConfirmation,
    MultiErrorsSnackbar,
    Table,
    UserRolesSelect
  },
  data() {
    return {
      tab: 0,
      err: '',
      errorsForSnackbar: [],
      showSnackErrors: false,
      success: '',
      valid: false,
      isEdit: false,
      loading: true,
      lock: false,
      library: {
        createdOn: '',
        id: '',
        modifiedOn: '',
        name: '',
        status: '',
        type: 'JS',
        roles: [],
        isGdprRelevant: false,
        size: '',
        filename: ''
      },
      allRoles: [],
      editRolesIds: [],
      useRolesIds: [],
      viewRolesIds: [],
      permissionsTypes: ['EDIT', 'USE', 'VIEW'],
      userRolesIds: [],
      userCanEdit: false,
      history: { items: [], meta: {} },
      isSuperUser: false,
      userCanDelete: false,
      triggerForceLogic: false,
      regularDeleteErrorsUsages: [],
      deleteSuccess: false,
      types: ['JS', 'GROOVY', 'PYTHON'],
      file: null,
      fileData: null
    }
  },
  computed: {
    typesFormatted () {
      return this.types.map((item) => {
        return { text: this.$lang.status[item], value: item }
      })
    },
    useRolePool() {
      return this.allRoles.filter((x) => !this.editRolesIds.includes(x.id))
    },
    viewRolePool() {
      return this.allRoles.filter((x) => !this.editRolesIds.includes(x.id) && !this.useRolesIds.includes(x.id))
    },
    formatHint() {
      return `${this.$lang.labels.createdOn}: ${this.$options.filters.formatDateTime(this.library.createdOn)}, ${this.$lang.labels.modifiedOn}: ${this.$options.filters.formatDateTime(this.library.modifiedOn)}, ID: ${this.library.id}`
    },
    isFormValid() {
      return this.valid && this.userCanEdit && !this.lock
    }
  },
  watch: {
    isFormValid(val) {
      this.formValid(val)
    },
    tab(val) {
      if (val === 2) {
        this.fetchHistory()
      }
    }
  },
  mounted() {
    bus.$on('saveResource', this.submit)
  },
  beforeDestroy() {
    bus.$off('saveResource', this.submit)
  },
  created() {
    this.isEdit = this.$route.name === 'libraryEdit'

    let user = null

    if (localStorage.userData) {
      user = JSON.parse(localStorage.userData)

      this.userRolesIds = user.roles.map((x) => x.id)
      this.isSuperUser = !!user.roles.find((x) => x.name === 'SUPER_USER')
      this.userCanDelete = !!user.roles.find((x) => x.name === 'LIBRARY_DELETER') || this.isSuperUser
    }

    this.init()
      .then(() => {
        if (this.isEdit && this.$route.params.id) {
          this.loading = true

          getLibrary({ id: this.$route.params.id })
            .then((res) => {
              this.library = res.data.data

              this.editRolesIds = this.library.roles.filter((x) => x.permissionType === 'EDIT').map((y) => y.role.id)
              this.useRolesIds = this.library.roles.filter((x) => x.permissionType === 'USE').map((y) => y.role.id)
              this.viewRolesIds = this.library.roles.filter((x) => x.permissionType === 'VIEW').map((y) => y.role.id)

              this.userCanEdit = this.isSuperUser ? true : !!this.editRolesIds.find((x) => this.userRolesIds.includes(x))

              this.loading = false
            })
            .catch((err) => {
              this.addSnackbar({
                message: err,
                timeout: 5000,
                color: 'error'
              })
              this.loading = false
            })
        } else {
          this.editRolesIds = user.roles.filter((x) => !x.isSystem).map((x) => x.id)
          this.userCanEdit = true
          if (this.$route.params.copy || this.$route.params.restore) {
            this.library = this.$route.params.copy ? JSON.parse(JSON.stringify(this.$route.params.copy)) : JSON.parse(JSON.stringify(this.$route.params.restore))
          }
          this.loading = false
        }
      })
  },
  methods: {
    ...mapActions('app', ['addSnackbar', 'formValid']),
    copyCreateResource,
    fileTypesBasedOnLibType() {
      switch (this.library.type) {
      case 'JS':
        return '.js, .zip'
      case 'GROOVY':
        return '.jar, .zip'
      case 'PYTHON':
        return '.py, .zip'
      default:
        return '.zip'
      }
    },
    downloadLibraryFunction() {
      this.loading = true
      this.lock = true
      downloadLibrary({ id: this.library.id })
        .then((res) => {
          if (res.status !== 200) {
            this.addSnackbar({
              message: res?.response?.data?.statusText ? res.response.data.statusText : this.$lang.errors.somethingWentWrong,
              timeout: 5000,
              color: 'error'
            })

            this.loading = false
            this.lock = false

            return
          }
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')

          link.href = url
          link.setAttribute('download', this.library.filename)
          document.body.appendChild(link)
          link.click()

          this.addSnackbar({
            message:  this.$lang.success.downloadSuccess,
            timeout: 5000,
            color: 'success'
          })

          this.loading = false
          this.lock = false
        })
        .catch((err) => {
          this.addSnackbar({
            message: err,
            timeout: 5000,
            color: 'error'
          })
        })
    },
    copyName() {
      if (this.library.name) {
        copy(this.library.name)
        this.addSnackbar({
          message:  this.$lang.success.copiedClipboard,
          timeout: 5000,
          color: 'success'
        })
      } else {
        this.addSnackbar({
          message:  this.$lang.errors.nothingToCopy,
          timeout: 5000,
          color: 'error'
        })
      }
    },
    startInput(name) {
      this.$refs[name].click()
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files

      if (!files.length) return
      this.fileData = files[0]
      this.file = `${this.$lang.labels.fileLoaded}: ${this.fileData.name}`
    },
    async uploadLibrary() {
      this.err = ''

      if (!this.fileData) return

      this.loading = true
      this.lock = true

      const data = new FormData()

      data.append('file', this.fileData)

      fetch(`/api/library/${this.library.id}/upload`, {
        method: 'post',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'x-auth-token': auth.getToken()
        },
        body: data
      }).then((response) => {
        return response.json()
      }).then((res) => {
        if (res && res.status !== 'SUCCESS') {
          const errorMessage = `${this.$lang.errors.libraryUpload}: ${res.statusText}`

          this.addSnackbar({
            message:  errorMessage,
            timeout: 5000,
            color: 'error'
          })
          this.loading = false
          this.lock = false

          return
        }

        this.library.filename = res.data.filename
        this.library.size = res.data.size

        this.loading = false
        this.lock = false

        this.file = ''
        this.fileData = ''

        this.addSnackbar({
          message:  this.$lang.success.libraryUploaded,
          timeout: 5000,
          color: 'success'
        })

      }).catch((err) => {
        this.addSnackbar({
          message: err,
          timeout: 5000,
          color: 'error'
        })
        this.loading = false
        this.lock = false
      })
    },
    deleteLibraryFunct(isForced = false) {
      this.triggerForceLogic = false
      this.regularDeleteErrorsUsages = []
      this.deleteSuccess = false
      deleteLibrary({ id: this.library.id, force: isForced })
        .then((res) => {
          if (res.status === 200) {
            this.deleteSuccess = true
            this.regularDeleteErrorsUsages = res.data.data.usages
          } else if (res.response.status === 400 && !res?.response?.data?.data?.usages) {
            this.addSnackbar({
              message: res.response.data.statusText,
              timeout: 5000,
              color: 'error'
            })
          } else {
            this.triggerForceLogic = true
            this.regularDeleteErrorsUsages = res.response.data.data.usages
          }
        })
        .catch((err) => {
          this.addSnackbar({
            message: err,
            timeout: 5000,
            color: 'error'
          })            })
    },
    fetchHistory(options) {
      if (!this.library.id) return

      this.history = { items: [], meta: {} }

      const obj = {}

      if (options) {
        if (options.options && options.options.itemsPerPage !== -1) {
          obj.page = options.resetPage ? 1 : options.options.page || 1
          obj.size = options.options.itemsPerPage || 25
        } else {
          obj.page = 1
          obj.size = 25
        }
      } else {
        obj.page = 1
        obj.size = 25
      }

      obj.resourceId = this.library.id
      obj.resourceType = 'LIBRARY'

      getHistories(obj)
        .then((res) => {
          this.history = res.data.data
        })
        .catch((err) => {
          this.addSnackbar({
            message: err,
            timeout: 5000,
            color: 'error'
          })            })
    },
    restoreLibrary(data) {
      this.library = data

      if (this.library && this.library.roles) {
        this.editRolesIds = this.library.roles.filter((x) => x.permissionType === 'EDIT').map((y) => y.role.id)
        this.useRolesIds = this.library.roles.filter((x) => x.permissionType === 'USE').map((y) => y.role.id)
        this.viewRolesIds = this.library.roles.filter((x) => x.permissionType === 'VIEW').map((y) => y.role.id)

        this.userCanEdit = this.isSuperUser ? true : !!this.editRolesIds.find((x) => this.userRolesIds.includes(x))
      }
    },
    init() {
      return new Promise((resolve) => {
        getRolesWithoutAuth()
          .then((roles) => {
            this.allRoles = roles
            resolve()
          })
          .catch((error) => {
            console.log(error)
            resolve()
          })
      })
    },
    async submit() {
      this.err = ''

      if (this.editRolesIds.length < 1 && !this.isSuperUser) {

        this.addSnackbar({
          message: this.$lang.errors.editRoleCreate,
          timeout: 5000,
          color: 'error'
        })
      }

      this.lock = true
      this.loading = true

      let res = null

      const { id } = this.library

      if (!this.isEdit) {
        delete this.library.id
        this.library.status = 'ACTIVE'
      }
      delete this.library.createdOn
      delete this.library.modifiedOn

      const tempRoles = []

      this.editRolesIds.forEach((x) => {
        tempRoles.push({ permissionType: 'EDIT', roleId: x })
      })

      this.useRolesIds.forEach((x) => {
        tempRoles.push({ permissionType: 'USE', roleId: x })
      })

      this.viewRolesIds.forEach((x) => {
        tempRoles.push({ permissionType: 'VIEW', roleId: x })
      })

      this.library.roles = tempRoles

      try {

        res = this.isEdit ? await updateLibrary({ id, body: this.library }) : await createLibrary({ body: this.library })

        if (res && res.status !== 200) {
          if (res?.response?.data?.data?.[0]?.error) {

            res?.response?.data?.data?.forEach((error) => {
              this.errorsForSnackbar.push({
                text: error.error,
                value: null
              })
            })
            this.showSnackErrors = true
          } else {
            const errorMessage = res?.response?.data?.statusText || (this.isEdit ? this.$lang.errors.libraryUpdate : this.$lang.errors.libraryCreate)

            this.addSnackbar({
              message: errorMessage,
              timeout: 5000,
              color: 'error'
            })
          }
          this.lock = false
          this.loading = false

          return
        }
        const successMessage = this.isEdit ? this.$lang.success.libraryUpdated : this.$lang.success.libraryCreated

        this.addSnackbar({
          message: successMessage,
          timeout: 5000,
          color: 'success'
        })

        if (!this.isEdit) {
          this.isEdit = true
          this.$router.push({
            name: 'libraryEdit',
            params: {
              id: res.data.data.id
            }
          })
        }

        if (this.fileData) {
          await this.uploadLibrary()
        } else {
          this.lock = false
          this.loading = false
        }

      } catch (err) {
        this.addSnackbar({
          message: err,
          timeout: 5000,
          color: 'error'
        })
        this.lock = false
      }
    },
    async changeStatus () {
      this.lock = true
      try {
        const res = this.library.status === 'ACTIVE' ? await disableLibrary({ id: this.library.id }) : await enableLibrary({ id: this.library.id })

        if (res.status !== 200) {
          this.addSnackbar({
            message: this.$lang.errors.libraryStatusUpdate,
            timeout: 5000,
            color: 'error'
          })
          this.lock = false

        }
        const successMessage = this.library.status !== 'ACTIVE' ? this.$lang.success.libraryActivated : this.$lang.success.libraryDeactivated

        this.addSnackbar({
          message: successMessage,
          timeout: 5000,
          color: 'success'
        })
        this.library.status = this.library.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'

        this.lock = false

      } catch (err) {
        this.addSnackbar({
          message: err,
          timeout: 5000,
          color: 'error'
        })
        this.lock = false
      }
    }
  }
}
</script>

<style>
.copy-name-icon .v-icon{
  color: #009fb7;
}

</style>
